/** Searchable, URL-controlled facet for server-filtered tables. */
import { Check, ChevronDown, Plus } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** One server-supported facet value. */
export interface TableFacetOption {
  value: string;
  label: string;
}

/** Render a compact facet without deriving options or counts from a loaded page. */
export function TableFacet({
  label,
  options,
  value,
  onChange,
  multiple = false,
  showLabel = true,
  trigger,
}: {
  label: string;
  options: TableFacetOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  multiple?: boolean;
  showLabel?: boolean;
  trigger?: ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  const groupId = useId();
  const [query, setQuery] = useState("");
  const selected = value ? (multiple ? value.split(",") : [value]) : [];
  const names = selected.map(
    (entry) => options.find((option) => option.value === entry)?.label ?? entry,
  );
  const visible = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );
  return (
    <Popover onOpenChange={() => setQuery("")}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="max-w-full gap-2" aria-label={label}>
            {selected.length ? (
              <Check className="size-3.5 text-primary" aria-hidden />
            ) : (
              <Plus className="size-3.5 text-muted-foreground" aria-hidden />
            )}
            <span className="min-w-0 truncate">
              {showLabel ? label : names.join(", ") || t("app.all")}
            </span>
            {showLabel && names.length ? (
              <span className="max-w-36 truncate border-s ps-2 text-muted-foreground">
                {names.length > 1 ? names.length : names[0]}
              </span>
            ) : null}
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" aria-label={label}>
        <Input
          type="search"
          name="facet-search"
          autoComplete="off"
          aria-label={`${t("app.search")}: ${label}`}
          placeholder={t("app.search")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div
          role={multiple ? "group" : "radiogroup"}
          aria-label={label}
          className="mt-2 max-h-64 space-y-0.5 overflow-y-auto"
        >
          {!multiple ? (
            <label className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent focus-within:ring-2 focus-within:ring-ring [@media(pointer:coarse)]:min-h-11">
              <input
                type="radio"
                name={groupId}
                checked={!selected.length}
                onChange={() => onChange(undefined)}
                className="size-4 shrink-0 accent-primary"
              />
              <span>{t("app.all")}</span>
            </label>
          ) : null}
          {visible.map((option) => (
            <label
              key={option.value}
              className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent focus-within:ring-2 focus-within:ring-ring has-[:checked]:bg-accent [@media(pointer:coarse)]:min-h-11"
            >
              <input
                type={multiple ? "checkbox" : "radio"}
                name={multiple ? undefined : groupId}
                checked={selected.includes(option.value)}
                onChange={(event) => {
                  const next = multiple
                    ? event.target.checked
                      ? [...selected, option.value]
                      : selected.filter((entry) => entry !== option.value)
                    : event.target.checked
                      ? [option.value]
                      : [];
                  onChange(next.length ? next.join(",") : undefined);
                }}
                className="size-4 shrink-0 accent-primary"
              />
              <span className="min-w-0 break-words">{option.label}</span>
            </label>
          ))}
          {!visible.length ? (
            <p role="status" className="p-3 text-sm text-muted-foreground">
              {t("tablePilot.noOptions")}
            </p>
          ) : null}
        </div>
        {selected.length ? (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full border-t"
            onClick={() => onChange(undefined)}
          >
            {t("app.clearFilters")}
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
